import React from "react";
import { Flex, Text } from "@jsq/ds/core";
import { SimpleFormRow } from "../SimpleFormRow";
import { type FieldGroupProps } from "./types";

/**
 * `FieldGroup` is a React component that renders a group of form fields with a title and optional description.
 *
 * This component is designed to be used as a container for form fields in a form. It uses a `Flex` component to layout its children in a column.
 *
 * @param {string} title - The title of the field group. This will be displayed above the form fields.
 * @param {string} [description] - An optional description for the field group. If provided, this will be displayed below the title and above the form fields.
 * @param {ReactNode} children - The form fields to be included in the field group. These will be rendered in a column below the title and description.
 *
 * @example
 * Here's an example of how to use the `FieldGroup` component:
 *
 * ```tsx
 * <FieldGroup title="Personal Information" description="Please enter your personal information.">
 *   <InputField name="firstName" label="First Name" />
 *   <InputField name="lastName" label="Last Name" />
 * </FieldGroup>
 * ```
 */
export const FieldGroup: React.FC<FieldGroupProps> = ({
  title,
  description,
  meta = {},
  children,
}: FieldGroupProps) => {
  const { headerType = "h4" } = meta;
  return (
    <Flex flexDirection="column" style={{ minWidth: 0 }}>
      {title && (
        <SimpleFormRow>
          <Text variant={headerType}>{title}</Text>
        </SimpleFormRow>
      )}
      {description && (
        <SimpleFormRow>
          <Text>{description}</Text>
        </SimpleFormRow>
      )}

      {children}
    </Flex>
  );
};
